/**
 * Shared helpers for pairing scenario sources with provider-side knowledge
 * documents. Tavus (flat documents) and D-ID (documents inside a knowledge
 * base) both key their pairings the same way, so the key derivation and the
 * URL resolution live here instead of in each provider folder.
 */
import { powtoonBE } from "@/lib/powtoon-be/client";
import type { ScenarioSourceItem } from "@/lib/powtoon-be/types";

/**
 * Stable key pairing a scenario source with the provider document created
 * from it. Uploads are keyed by their Powtoon object id, URLs by the URL
 * itself, so a re-save always finds the existing document instead of minting
 * a new one (which would leave orphans upstream).
 */
export function sourceKeyOf(item: ScenarioSourceItem): string | null {
  if (item.kind === "upload") return item.object_id ? `upload:${item.object_id}` : null;
  if (item.kind === "url") return item.url.trim() ? `url:${item.url.trim()}` : null;
  // Pasted text has no fetchable URL — it can't become a provider document.
  return null;
}

/** Human label for pickers / status lines. */
export function sourceLabelOf(item: ScenarioSourceItem): string {
  if (item.kind === "upload") return item.file_name;
  if (item.kind === "url") return item.url;
  return "Pasted text";
}

/** Sources that can be registered upstream (uploaded files and URLs). */
export function documentCandidates(inputs: ScenarioSourceItem[]) {
  return inputs
    .map((item) => ({ item, key: sourceKeyOf(item) }))
    .filter((c): c is { item: ScenarioSourceItem; key: string } => !!c.key);
}

/**
 * Absolute URL the provider can fetch the source from. Uploads resolve to the
 * Powtoon attachment download URL; URL sources are used as-is.
 */
export async function resolveSourceDownloadUrl(
  item: ScenarioSourceItem,
  scenarioId?: string,
): Promise<string> {
  if (item.kind === "url") return item.url.trim();
  if (item.kind !== "upload") {
    throw new Error("This source can't be used as a knowledge document.");
  }
  if (!scenarioId) {
    throw new Error("Save the scenario once before attaching a knowledge document.");
  }
  const att = await powtoonBE.attachments.get(scenarioId, item.object_id);
  const url = att?.file ?? att?.file_url;
  if (!url) {
    throw new Error(`"${item.file_name}" has no downloadable URL — re-upload the file.`);
  }
  return url;
}
